import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  IconButton, 
  Typography, 
  Avatar, 
  Chip,
  List,
  ListItem,
  Paper,
  useTheme
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import CustomScrollbar from './CustomScrollbar';
import { useAiChat } from 'services/AiBots/useAiChat';
import { UserBot } from '../Bots/StrategyBuilder/types';
import { TGetConfigFromAIParams } from 'types/ApiServicesTypes';
import useGetProfile from 'services/Menu/useGetProfile';
import { Person } from '@mui/icons-material';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

interface AiBotsChatProps {
  height?: string;
  setStrategy: (strategy: UserBot) => void;
  setNewBotName: (name: string) => void;
}

const AiBotsChat: React.FC<AiBotsChatProps> = ({ height = '100%', setStrategy, setNewBotName}) => {
  const theme = useTheme();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Hello! I\'m your SpeedBot trading assistant. I can help you with market analysis, strategy recommendations, risk management, and trading insights. What would you like to know?',
      sender: 'ai',
      timestamp: new Date(),
    },
  ]);
  const mutation = useAiChat();
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [online, setOnline] = useState(true);
  const [sessionFolder, setSessionFolder] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { data: profileDetails } = useGetProfile();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const generateAIResponse = (userInput: string): string => {
    const lowerInput = userInput.toLowerCase();
    
    if (lowerInput.includes('hello') || lowerInput.includes('hi')) {
      return 'Hello! How can I assist you with your trading today?';
    }
    
    if (lowerInput.includes('market') || lowerInput.includes('analysis')) {
      return 'I can help you with market analysis! I can provide insights on market trends, technical indicators, and fundamental analysis. What specific market or asset would you like me to analyze?';
    }
    
    if (lowerInput.includes('strategy') || lowerInput.includes('strategy')) {
      return 'I can help you develop trading strategies! I can suggest strategies based on your risk tolerance, market conditions, and trading style. Would you like me to recommend some strategies?';
    }
    
    if (lowerInput.includes('risk') || lowerInput.includes('management')) {
      return 'Risk management is crucial for successful trading. I can help you with position sizing, stop-loss strategies, and portfolio diversification. What aspect of risk management would you like to discuss?';
    }
    
    if (lowerInput.includes('technical') || lowerInput.includes('indicator')) {
      return 'Technical indicators are powerful tools for market analysis. I can help you understand indicators like RSI, MACD, Moving Averages, Bollinger Bands, and more. Which indicator would you like to learn about?';
    }
    
    if (lowerInput.includes('crypto') || lowerInput.includes('bitcoin')) {
      return 'Cryptocurrency trading has unique characteristics compared to traditional markets. I can help you understand crypto market dynamics, volatility patterns, and trading strategies specific to digital assets.';
    }
    
    return 'Thank you for your message! I\'m here to help with your trading questions. I can assist with market analysis, strategy development, risk management, and more. Please let me know what specific topic you\'d like to explore.';
  };

  const handleSendMessage = () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue.trim(),
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    if (mutation && profileDetails) {
      const params : TGetConfigFromAIParams = {
        prompt: inputValue.trim(),
        user_id: profileDetails.user_id,
        session_name: sessionFolder,
      }
      if(sessionFolder && sessionFolder.length > 0){
        params.session_name = sessionFolder;
      }
        mutation?.mutate(params,
        {
            onSuccess: res => {
              if(res.session_name && res.session_name !== sessionFolder){
                setSessionFolder(res.session_name);
              }
              if (res.files && res.files.understanding) {
                const aiResponse: Message = {
                    id: (Date.now() + 1).toString(),
                    text: res.files.understanding,
                    sender: 'ai',
                    timestamp: new Date(),
                  };
                  if(res.files && res.files.output_config){
                    setStrategy(null as unknown as UserBot);
                    setStrategy(res.files.output_config as unknown as UserBot);
                    // setNewBotName(res.strategy_name);
                  }
                  setMessages(prev => [...prev, aiResponse]);
                  setIsLoading(false);
              }else{  
                let msg = 'I\'m sorry, I couldn\'t understand your message. Please try again.'
                if(res && res.error_msg){
                  if(res.error_msg && res.error_msg.includes('Request timeout')){
                    msg = 'Request timeout. Please try again.'
                  }
                  else if(res.error_msg && res.error_msg.includes('Rate limit exceeded')){
                    msg = 'You have reached the rate limit. Please try again later.'
                    setOnline(false);
                  }
                }else if(res && res.toString() && res.toString().includes('500') || res && res.toString() && res.toString().includes('Unknown error')){
                  // msg = 'Heyyy, currently i am offline. Please try again later.'
                  msg = `Hey ${profileDetails.name || 'User'}, AI Chat isn’t part of your plan yet. Contact our team to unlock it!`
                  setOnline(false);
                }
                const aiResponse: Message = {
                  id: (Date.now() + 1).toString(),
                  text: msg,
                  sender: 'ai',
                  timestamp: new Date(),
                };
                setMessages(prev => [...prev, aiResponse]);
                setIsLoading(false);
              }
             },
             onError: err => {
                setOnline(false);
             }
          },);
    } else {
    // Simulate AI response
    setTimeout(() => {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: generateAIResponse(inputValue.trim()),
          sender: 'ai',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsLoading(false);
      }, 2000);
    }

  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        height,
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        backgroundColor: 'background.paper'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <SmartToyIcon />
          </Avatar>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">Cruise </Typography>
              <Box
                sx={{
                  px: 0.8,
                  py: 0.7,
                  pt: 0.8,
                  mt: 0.5,
                  // border: `0.5px solid ${theme.palette.primary.main}`,
                  backgroundColor: theme.palette.primary.dark,
                  color: theme.palette.primary.contrastText,
                  borderRadius: 1,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  lineHeight: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 20,
                  height: 12,
                }}
              >
                1.0
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: online 
                      ? 'success.main' 
                      : 'error.main',
                  animation: isLoading ? 'pulse 1.5s ease-in-out infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 },
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                  {online ? 'Online' : 'Offline'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Messages */}
      <CustomScrollbar height="100%">
        <Box sx={{ 
          flex: 1, 
          p: 2,
          backgroundColor: 'grey.50',
          minHeight: '100%'
        }}>
        <List>
          {messages.map((message) => (
            <ListItem key={message.id} sx={{ 
              display: 'flex', 
              justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
              px: 0
            }}>
              <Box sx={{
                display: 'flex',
                flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                alignItems: 'flex-start',
                gap: 1,
                maxWidth: '70%',
              }}>
                <Avatar
                  sx={{
                    bgcolor: message.sender === 'user' ? 'secondary.main' : 'primary.main',
                    width: 32,
                    height: 32,
                    mt: 0.5,
                  }}
                >
                  {message.sender === 'user' ? <Person /> : <SmartToyIcon />}
                </Avatar>
                
                <Paper
                  elevation={0}
                  sx={{
                    p: 1,
                    backgroundColor: message.sender === 'user' ? '#D9EAFD' : 'background.paper',
                    color: message.sender === 'user' ? 'black' : 'text.primary',
                    borderRadius: 1.5,
                    border: '1px solid #D9EAFD',
                    maxWidth: '100%',
                    wordWrap: 'break-word',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.text}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.5,
                      display: 'block',
                      opacity: 0.7,
                      color: message.sender === 'user' ? 'black' : 'text.primary',
                      textAlign: message.sender === 'user' ? 'right' : 'left',
                    }}
                  >
                    {message.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Paper>
              </Box>
            </ListItem>
          ))}
        </List>
        
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <SmartToyIcon sx={{ fontSize: 16 }} />
            </Avatar>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              backgroundColor: 'background.paper',
              borderRadius: 1,
              px: 1.5,
              py: 0.5,
              border: '1px solid #D9EAFD'
            }}>
              <Typography variant="body2" color="text.secondary">
                Thinking
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {[0, 1, 2].map((index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'text.secondary',
                      animation: `wavyDot 1.4s ease-in-out infinite`,
                      animationDelay: `${index * 0.2}s`,
                      '@keyframes wavyDot': {
                        '0%, 50%, 100%': {
                          transform: 'translateY(0px)',
                          opacity: 0.4,
                        },
                        '25%, 75%': {
                          transform: 'translateY(-6px)',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}
        <div ref={messagesEndRef} />
        </Box>
      </CustomScrollbar>

      {/* Input */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message here..."
          disabled={isLoading || !online}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />
        <IconButton
          onClick={handleSendMessage}
          disabled={!inputValue.trim() || isLoading}
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            alignSelf: 'center',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            '&:disabled': {
              bgcolor: 'grey.300',
              color: 'grey.500',
            },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default AiBotsChat; 